<template>
  <Teleport to="body">
    <dialog-base :show="show"
                 @hide="onHide"
                 :is-draggable="true"
                 :close-on-click="true"
                 :styled="false"
                 size="lg"
                 test-id="card-kp-modal">
      <!-- the ticket's prototype header (DM-11385) -->
      <template #header>
        <div class="kp-header" data-testid="card-kp-header">
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#003C8F" stroke-width="1.8"><path d="M6 3h9l4 4v14H6z"></path></svg>
          <span class="kp-title">Knowledge Panel</span>
          <span class="kp-when">{{ headerWhen }}</span>
        </div>
      </template>
      <template #content>
        <div class="card">
          <KP-card
                   v-if="term && term.html"
                   :key="term.id"
                   :is-hide="!!term.hide"
                   :term-data="term"
                   :card-meta="getCardMeta(term)"
                   :should-zoom="false"
                   :is-links-active="true"
                   :show-header="false"
                   :fill-width="true"
                   :aioverview="elementData?.kp?.aioverview"
                   :aio-ts="Date.parse(cardMeta.datetime) / 1000"
                   @showCard="show" />
        </div>
      </template>
    </dialog-base>
  </Teleport>
</template>

<script>
import { unref } from "vue";
import { mapGetters } from "vuex";
import DialogBase from "@/components/modals/DialogBase.vue";
import KPCard from "@/components/KPCard.vue";
import { IMPACT_QUERY_DATE_FORMAT } from "@/constants";

export default {
  name: "KpDialog",
  emits: ["hide", "setShowKpDialog"],
  components: {
    DialogBase,
    KPCard,
  },
  inject: {
    cardMetaRef: { from: 'cardMeta', default: () => ({}) }
  },
  props: {
    show: Boolean,
    elementData: Object,
  },
  computed: {
    ...mapGetters("routeParams", {
      ctid: "ctid",
      resultset: "resultset",
      client: "client",
    }),
    cardMeta() {
      return unref(this.cardMetaRef) || {};
    },
    // "November 2, 2026 · Portland ME"
    headerWhen() {
      const date = this.cardMeta.datetime ? this.$dateTime.fromISO(this.cardMeta.datetime).toFormat("LLLL d, yyyy") : "";
      return [date, this.cardMeta.location].filter(Boolean).join(" · ");
    },
  },
  data() {
    return {
      key: Date.now() + "kp-dialog",
      eventMounted: false,
      term: {},
    };
  },
  methods: {
    // DM-11385: a source row opens the Markup dialog on top of the panel; a click in it is not
    // a click away from the panel. The event's path, not its target: a clicked dropdown item is
    // already gone from the page when this runs.
    onHide(event) {
      const path = event?.composedPath?.() || [];
      if (path.some((el) => el?.getAttribute?.('data-testid') === 'card-markup-modal')) {
        return;
      }
      this.$emit('hide');
    },
    getCardMeta(term) {
      return {
        id: term.id,
        datetime: term.date,
        location: term.term?.location,
        country: term.term?.country,
        lang: term.term?.lang,
        engine: term.term?.engine,
        device: term.term?.device,
        keyword: term.term?.term,
        tid: term.term?.tid,
      };
    },
    processKP() {
      const date = this.$dateTime
        .fromISO(this.cardMeta.datetime)
        .toFormat(IMPACT_QUERY_DATE_FORMAT);

      this.$api
        .getTermRange(this.client.clientid, this.ctid, date, date, this.resultset)
        .then((res) => {
          // Loose tid compare: cardMeta.tid comes from the card document (number),
          // term_range returns its own rows and the two have disagreed in type — a
          // strict === then silently threw "Term not found." and the dialog opened
          // blank with the stored page never requested (observed 2026-08-06, tid
          // 99750). Compare as strings so either representation matches.
          const wantTid = String(this.cardMeta.tid ?? "");
          this.term = res.data.find((t) => String(t?.term?.tid ?? "") === wantTid);
          if (!this.term) {
            // Self-diagnosing: say what was asked for and what came back, so a miss
            // is actionable instead of an opaque "Term not found."
            console.error("KP: no term matched", {
              wantTid,
              wantType: typeof this.cardMeta.tid,
              returned: (res.data || []).map((t) => t?.term?.tid),
              date,
              resultset: this.resultset,
              ctid: this.ctid,
            });
          }

          if (this.term) {
            const hashId = this.stringToHash(this.term.date + this.term.term.tid);
            this.term.id = hashId;

            const termdate = this.$dateTime.fromISO(this.term.date).toMillis() / 1000;

            return this.$api.getKpHtml(
              this.term.term.clientid,
              this.term.term.tid,
              termdate,
              1,
              0,
              1
            );
          } else {
            throw new Error("Term not found.");
          }
        })
        .then((res) => {
          if (res) {
            this.term.html = res.data;
            this.term.hasKP = true;
          }
        })
        .catch((error) => {
          console.error("Error processing KP:", error);
        })
        .finally(() => {
          this.$emit("setShowKpDialog", true);
        });
    },
  },
  watch: {
    show(currentValue) {
      if (currentValue === true) {
        if (!this.term.html) {
          this.processKP();
        }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.kp-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 8px;
  padding: 12px 40px 0 14px;
  font-size: 12px;
  color: #212529;

  .kp-title {
    font-size: 15px;
    font-weight: 600;
    flex: 1 1 auto;
    white-space: nowrap;
  }

  // on a narrow panel the date goes under the title rather than splitting it
  .kp-when {
    color: #40454C;
    white-space: nowrap;
  }
}

.card {
  border: 0;
  min-height: 716px;
}

:deep(.card-content) {
  max-height: 600px;
  overflow: hidden;
  overflow-y: scroll;
}
</style>